/**
 * FRONT-1747: the form the Quick OK card's "Edit" opens. Markup only — every
 * change goes through the pure transforms in `./approval-edit-draft`, which is
 * where the behaviour is tested.
 *
 * Two shapes: a memory save (one block per entry — name, type and facts as
 * label/value rows; a batch save also lets an entry be removed) and a
 * document rewrite (title plus a text box that grows). An update to an entry
 * that already exists shows only what the proposal is about to change; its
 * other facts stay on the Memory page.
 */
import { PlusIcon, XIcon } from '@phosphor-icons/react';
import { Button } from '~/components/ui/button';
import { Input } from '~/components/ui/input';
import { Textarea } from '~/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '~/components/ui/select';
import { ENTITY_TYPES, ENTITY_TYPE_LABELS } from '~/lib/entity-types';
import {
  addEntityFact,
  entityHeading,
  existingEntryNote,
  removeEntity,
  removeEntityFact,
  setEntityName,
  setEntityType,
  updateEntityFact,
  type ApprovalDraft,
  type EntityDraft,
} from './approval-edit-draft';

type MemoryDraft = Extract<ApprovalDraft, { kind: 'memory' }>;

const LABEL = 'text-[length:var(--text-xs)] font-medium text-muted-foreground';

function EntityBlock({
  entity,
  batch,
  draft,
  onChange,
  lockType,
}: {
  entity: EntityDraft;
  batch: boolean;
  draft: MemoryDraft;
  onChange: (next: ApprovalDraft) => void;
  lockType: boolean;
}) {
  const heading = entityHeading(entity);
  return (
    <div
      data-testid="approval-edit-entity"
      className={
        batch
          ? 'rounded-lg border border-border bg-background/70 p-2.5 space-y-2'
          : 'space-y-2'
      }
    >
      {batch && (
        <div className="flex items-center justify-between">
          <span className={LABEL}>{heading}</span>
          <Button
            size="sm"
            variant="ghost"
            className="h-7 text-[length:var(--text-xs)] text-muted-foreground hover:text-foreground"
            onClick={() => onChange(removeEntity(draft, entity.index))}
          >
            Remove
          </Button>
        </div>
      )}
      {(entity.name !== null || entity.type !== null) && (
        <div className="grid grid-cols-2 gap-2">
          {entity.name !== null && (
            <label className="flex flex-col gap-1">
              <span className={LABEL}>Name</span>
              <Input
                className="h-8"
                value={entity.name}
                aria-invalid={!entity.name.trim() || undefined}
                onChange={(e) => onChange(setEntityName(draft, entity.index, e.target.value))}
              />
            </label>
          )}
          {entity.type !== null && (
            <div className="flex flex-col gap-1">
              <span className={LABEL}>Type</span>
              {lockType ? (
                <span data-testid="approval-edit-type-locked" className="flex h-8 items-center text-[length:var(--text-sm)]">
                  {(ENTITY_TYPE_LABELS as Record<string, string>)[entity.type] ?? entity.type}
                </span>
              ) : (
                <Select value={entity.type} onValueChange={(v) => onChange(setEntityType(draft, entity.index, v))}>
                  <SelectTrigger className="h-8 w-full" aria-label="Type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ENTITY_TYPES.map((t) => (
                      <SelectItem key={t} value={t}>
                        {ENTITY_TYPE_LABELS[t]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          )}
        </div>
      )}
      {entity.existing && entity.name === null && (
        <p className="text-[length:var(--text-sm)] text-muted-foreground text-pretty">
          {existingEntryNote(entity, batch)}
        </p>
      )}
      <div className="space-y-1.5">
        <div>
          <span className={LABEL}>Facts</span>
          <p className="text-[length:var(--text-xs)] text-muted-foreground text-pretty">
            One fact per row: what it is on the left, the detail on the right.
          </p>
        </div>
        {entity.facts.map((f) => (
          <div key={f.uid} className="flex items-center gap-1.5">
            <Input
              className="h-8 w-[38%]"
              placeholder="e.g. Role"
              aria-label="Fact label"
              value={f.key}
              onChange={(e) => onChange(updateEntityFact(draft, entity.index, f.uid, { key: e.target.value }))}
            />
            <Input
              className="h-8 flex-1"
              placeholder="e.g. Head of Procurement"
              aria-label="Fact value"
              value={f.value}
              onChange={(e) => onChange(updateEntityFact(draft, entity.index, f.uid, { value: e.target.value }))}
            />
            <Button
              size="icon"
              variant="ghost"
              className="relative size-8 shrink-0 text-muted-foreground hover:text-foreground after:absolute after:-inset-1 after:content-['']"
              aria-label="Remove this fact"
              onClick={() => onChange(removeEntityFact(draft, entity.index, f.uid))}
            >
              <XIcon size={14} />
            </Button>
          </div>
        ))}
        <Button
          size="sm"
          variant="ghost"
          className="h-7 px-2 text-[length:var(--text-xs)] text-muted-foreground hover:text-foreground"
          onClick={() => onChange(addEntityFact(draft, entity.index))}
        >
          <PlusIcon size={12} weight="bold" /> Add a fact
        </Button>
      </div>
    </div>
  );
}

export function ApprovalEditFormFields({
  draft,
  onChange,
  lockType = false,
}: {
  draft: ApprovalDraft;
  onChange: (next: ApprovalDraft) => void;
  /** Show the entry's type as plain text instead of a picker (the Inbox's Remember editor: the server refuses a type change). */
  lockType?: boolean;
}) {
  if (draft.kind === 'document') {
    return (
      <div data-testid="approval-edit-document" className="space-y-2">
        <label className="flex flex-col gap-1">
          <span className={LABEL}>Title</span>
          <Input
            className="h-8"
            value={draft.title}
            aria-invalid={!draft.title.trim() || undefined}
            onChange={(e) => onChange({ ...draft, title: e.target.value })}
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className={LABEL}>Content</span>
          <Textarea
            className="min-h-32 max-h-80 overflow-y-auto"
            value={draft.content}
            onChange={(e) => onChange({ ...draft, content: e.target.value })}
          />
        </label>
        <p className="text-[length:var(--text-xs)] text-muted-foreground">This replaces the whole document.</p>
      </div>
    );
  }
  if (draft.entities.length === 0) {
    return (
      <p className="rounded-lg bg-muted/40 px-3 py-2 text-[length:var(--text-sm)] text-muted-foreground text-pretty">
        Nothing left to save. Saying No now just skips it.
      </p>
    );
  }
  const batch = draft.batch;
  return (
    <div className="space-y-2">
      {draft.entities.map((e) => (
        <EntityBlock key={e.index} entity={e} batch={batch} draft={draft} onChange={onChange} lockType={lockType} />
      ))}
    </div>
  );
}
